<script setup>
/**
 * 厂商详情 · key 池区块 —— **只有配置列**:脱敏 key、label、权重、并发上限、TPM/RPM 上限、优先级、
 * 启用状态。用量、饱和度、冷却剩余、清除冷却是运行时数据(要读 Redis),整整一页在 `/llm/runtime`,
 * 这里只给一个按钮跳过去 —— 同一张表画两遍迟早两套口径。
 *
 * 停用/删除某厂商最后一把 enabled key 会被后端预检拒(409)—— 这里不写一段话解释,直接把那两个
 * 按钮 `disabled`(hover 一句「至少留一把」)。后端仍是防线,但人先不会去撞它。
 *
 * 别的写入被拒时:确认框里当场显示后端原文,同时在表格上方留一条常驻错误条(确认框一关就什么都
 * 看不见的话,人只会以为自己点漏了),错误条上的重试按钮重跑上一次失败的操作。
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Activity, Plus } from '@lucide/vue'
import { Badge, Button, ConfirmAction, EmptyState, Section, Table, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { keyFieldsOf, toKeyEnabledBody } from '../llm-key-form.js'
import KeyFormModal from './KeyFormModal.vue'

const props = defineProps({
  /** key 池命名空间 `<能力>:<接入代码>`（如 `text:volcengine`、`avatar:vidu`）：标题与表单字段都按它定。 */
  vendor: { type: String, required: true },
  /** 能力接入的 base_url：key 不填时继承的就是它（只有 text 池用）。 */
  providerBaseUrl: { type: String, default: null },
  keys: { type: Array, required: true },
  /** 保存一把 key（新增、编辑、启停）：能力接入池与服务凭据池走各自的接口。 */
  saveKey: { type: Function, required: true },
  deleteKey: { type: Function, required: true },
  /** 模型能力池至少留一把启用的 key；服务凭据池可以清空（数字人只是不可用）。 */
  requireEnabledKey: { type: Boolean, default: true },
})

const emit = defineEmits(['changed'])

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const actionError = ref('')
/** 上一次失败的操作,供错误条上的「重试」重跑。 */
const lastAction = ref(null)
const retrying = ref(false)

const modalOpen = ref(false)
/** null = 新增一把 key;非空 = 编辑这一行。 */
const editingKey = ref(null)

/** 一页渲染多个池（每能力一个），标题必须带能力名——四个只写「密钥池」的卡片看起来像同一份数据。 */
const poolTitle = computed(() => {
  const [capability, code] = props.vendor.split(':')
  return [t('visualGameAdmin.llm.providers.keys.title'), t(`visualGameAdmin.llm.capabilities.types.${capability}`), code].join(' · ')
})
const fields = computed(() => keyFieldsOf(props.vendor))

/** 媒体调用不产生 token 用量，TPM 上限永不触发——非 text 池不展示这一列。 */
const isTextPool = computed(() => fields.value.max_tpm)

const columns = computed(() => [
  { key: 'api_key_masked', label: t('visualGameAdmin.llm.providers.keys.table.key'), width: '190px', mono: true },
  { key: 'label', label: t('visualGameAdmin.llm.providers.keys.table.label'), width: '130px' },
  { key: 'weight', label: t('visualGameAdmin.llm.providers.keys.table.weight'), width: '70px', align: 'right', mono: true },
  { key: 'max_concurrency', label: t('visualGameAdmin.llm.providers.keys.table.concurrency'), width: '100px', align: 'right', mono: true },
  { key: 'max_tpm', label: t('visualGameAdmin.llm.providers.keys.table.tpm'), width: '100px', align: 'right', mono: true },
  { key: 'max_rpm', label: t('visualGameAdmin.llm.providers.keys.table.rpm'), width: '100px', align: 'right', mono: true },
  { key: 'priority', label: t('visualGameAdmin.llm.providers.keys.table.priority'), width: '80px', align: 'right', mono: true },
  { key: 'enabled', label: t('visualGameAdmin.llm.providers.keys.table.enabled'), width: '90px' },
  { key: 'actions', label: t('visualGameAdmin.llm.providers.keys.table.actions'), width: '190px' },
].filter((column) => isTextPool.value || column.key !== 'max_tpm'))

/** 停用/删除唯一那把 enabled key = 运行期没 key 可用。按钮直接 disabled,不靠一段说明。 */
const enabledCount = computed(() => props.keys.filter((key) => key.enabled).length)

/** @param {import('../../../api/llm/index.js').KeyRow} row */
function isLastEnabled(row) {
  return props.requireEnabledKey && row.enabled && enabledCount.value === 1
}

/**
 * 把一次写操作包成 `ConfirmAction` 要的 thunk,并记下来供重试。
 * @param {() => Promise<unknown>} run
 */
function confirmable(run) {
  return async () => {
    actionError.value = ''
    lastAction.value = run
    await run()
  }
}

/** @param {import('../../../api/llm/index.js').KeyRow} row */
function setKeyEnabled(row, enabled) {
  return props.saveKey(toKeyEnabledBody(row, enabled, fields.value))
}

/** 启用不是危险操作,不需要二次确认;失败照样要看得见。 */
async function enableKey(row) {
  const run = () => setKeyEnabled(row, true)
  actionError.value = ''
  lastAction.value = run
  try {
    await run()
    onActionDone()
  } catch (err) {
    onActionFailed(err)
  }
}

function onActionDone() {
  actionError.value = ''
  lastAction.value = null
  toast.success(t('visualGameAdmin.llm.providers.toast.saved'))
  emit('changed')
}

function onKeyDeleted() {
  actionError.value = ''
  lastAction.value = null
  toast.success(t('visualGameAdmin.llm.providers.toast.keyDeleted'))
  emit('changed')
}

/** @param {Error} err */
function onActionFailed(err) {
  actionError.value = err.message
  toast.error(t('visualGameAdmin.llm.providers.toast.failed'), { duration: 0 })
}

async function retryLast() {
  if (!lastAction.value) throw new Error('KeyPool.retryLast: no failed action to retry')
  retrying.value = true
  try {
    await lastAction.value()
    onActionDone()
  } catch (err) {
    onActionFailed(err)
  } finally {
    retrying.value = false
  }
}

function openCreate() {
  editingKey.value = null
  modalOpen.value = true
}

/** 当前请求数 / 饱和度 / 冷却 / 认证失败都在运行时页，不在这里重复展示。 */
function openRuntime() {
  router.push({ name: 'llm-runtime' })
}

/** @param {import('../../../api/llm/index.js').KeyRow} row */
function openEdit(row) {
  editingKey.value = row
  modalOpen.value = true
}

function onKeySaved() {
  modalOpen.value = false
  toast.success(t('visualGameAdmin.llm.providers.toast.saved'))
  emit('changed')
}

/** @param {number|null} limit */
function formatLimit(limit) {
  return limit === null ? t('visualGameAdmin.llm.providers.keys.unlimited') : limit.toLocaleString('en-US')
}
</script>

<template>
  <Section
    :title="poolTitle"
    :padded="keys.length === 0"
    data-insp-path="apps/visual-game-admin/src/pages/llm/provider/KeyPool.vue#key-pool"
  >
    <template #actions>
      <Button
        variant="ghost"
        size="sm"
        @click="openRuntime"
      >
        <Activity :size="16" />
        {{ t('visualGameAdmin.llm.providers.keys.runtime') }}
      </Button>
      <Button
        variant="primary"
        size="sm"
        @click="openCreate"
      >
        <Plus :size="16" />
        {{ t('visualGameAdmin.llm.providers.keys.add') }}
      </Button>
    </template>

    <div
      v-if="actionError"
      class="key-pool__error"
      role="alert"
    >
      <p class="vga-error">
        {{ actionError }}
      </p>
      <Button
        variant="secondary"
        size="sm"
        :loading="retrying"
        @click="retryLast"
      >
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>

    <EmptyState
      v-if="keys.length === 0"
      :title="t('visualGameAdmin.llm.providers.keys.empty')"
    >
      <template #action>
        <Button
          variant="primary"
          @click="openCreate"
        >
          {{ t('visualGameAdmin.llm.providers.keys.add') }}
        </Button>
      </template>
    </EmptyState>

    <Table
      v-else
      :columns="columns"
      :rows="keys"
    >
      <template #label="{ row }">
        {{ row.label ?? '-' }}
      </template>
      <template #max_tpm="{ row }">
        {{ formatLimit(row.max_tpm) }}
      </template>
      <template #max_rpm="{ row }">
        {{ formatLimit(row.max_rpm) }}
      </template>
      <template #enabled="{ row }">
        <Badge :variant="row.enabled ? 'success' : 'neutral'">
          {{ t(`visualGameAdmin.llm.providers.status.${row.enabled ? 'enabled' : 'disabled'}`) }}
        </Badge>
      </template>
      <template #actions="{ row }">
        <div class="key-pool__actions">
          <Button
            variant="ghost"
            size="sm"
            @click="openEdit(row)"
          >
            {{ t('visualGameAdmin.llm.providers.keys.actions.edit') }}
          </Button>

          <!-- 最后一把 enabled key:停用和删除都会让运行期没 key 可用 —— 按钮直接停掉。 -->
          <template v-if="isLastEnabled(row)">
            <Button
              variant="ghost"
              size="sm"
              disabled
              :title="t('visualGameAdmin.llm.providers.keys.lastEnabled')"
            >
              {{ t('visualGameAdmin.llm.providers.keys.actions.disable') }}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled
              :title="t('visualGameAdmin.llm.providers.keys.lastEnabled')"
            >
              {{ t('visualGameAdmin.llm.providers.keys.actions.delete') }}
            </Button>
          </template>

          <template v-else>
            <ConfirmAction
              v-if="row.enabled"
              danger
              :label="t('visualGameAdmin.llm.providers.keys.actions.disable')"
              :confirm-text="t('visualGameAdmin.llm.providers.keys.confirm.disable')"
              :cancel-label="t('visualGameAdmin.common.cancel')"
              :ok-label="t('visualGameAdmin.common.confirm')"
              :pending-label="t('visualGameAdmin.common.loading')"
              :action="confirmable(() => setKeyEnabled(row, false))"
              @done="onActionDone"
              @error="onActionFailed"
            />
            <Button
              v-else
              variant="secondary"
              size="sm"
              @click="enableKey(row)"
            >
              {{ t('visualGameAdmin.llm.providers.keys.actions.enable') }}
            </Button>
            <ConfirmAction
              danger
              :label="t('visualGameAdmin.llm.providers.keys.actions.delete')"
              :confirm-text="t('visualGameAdmin.llm.providers.keys.confirm.delete')"
              :cancel-label="t('visualGameAdmin.common.cancel')"
              :ok-label="t('visualGameAdmin.common.confirm')"
              :pending-label="t('visualGameAdmin.common.loading')"
              :action="confirmable(() => deleteKey(row.id))"
              @done="onKeyDeleted"
              @error="onActionFailed"
            />
          </template>
        </div>
      </template>
    </Table>

    <KeyFormModal
      v-model:open="modalOpen"
      :vendor="vendor"
      :provider-base-url="providerBaseUrl"
      :save-key="saveKey"
      :key-row="editingKey"
      @saved="onKeySaved"
    />
  </Section>
</template>

<style scoped>
.key-pool__error {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  margin: var(--drama-space-lg);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-danger);
  border-radius: var(--drama-radius-sm);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
}

.key-pool__error p {
  /* min-width: 0 —— flex 项默认不缩到内容以下,后端那条长错误消息会被裁掉半句。 */
  flex: 1;
  min-width: 0;
  margin: 0;
  line-height: var(--drama-line-height-normal);
}

.key-pool__actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}
</style>
